/**
 * Form body for the lander edit dialog — Name + URL (with tokens panel) +
 * Group + Language. The shell dialog keeps the data fetch + save wiring.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { AlertTriangle } from 'lucide-react';

import { Input } from '@/components/ui/form/input/Input';
import { SearchableSelect } from '@/components/ui/form/input/SearchableSelect';
import type { BinomLandingDetail } from '@/hooks/binom';

import OfferEditField from '../OfferEditField';
import BinomUrlTokensPanel from '../tokens/BinomUrlTokensPanel';

export interface BinomLanderEditBodyProps {
  form: BinomLandingDetail;
  onChange: (next: BinomLandingDetail) => void;
  groupOptions: Array<{ value: string; label: string }>;
  languageOptions: Array<{ value: string; label: string }>;
}

const BinomLanderEditBody: FC<BinomLanderEditBodyProps> = ({
  form,
  onChange,
  groupOptions,
  languageOptions,
}) => (
  <div className="space-y-3">
    <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 p-2 text-[11px] text-warning">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>
        This edits the GLOBAL landing entity in Binom — every campaign / path
        using it picks up the change.
      </span>
    </div>
    <OfferEditField label="Name">
      <Input
        value={form.name ?? ''}
        onChange={(e) =>
          onChange({ ...form, name: e.target.value } as BinomLandingDetail)
        }
        className="h-8 text-xs"
      />
    </OfferEditField>
    <OfferEditField label="URL">
      <div className="flex flex-col gap-1">
        <Input
          value={form.url ?? ''}
          onChange={(e) =>
            onChange({ ...form, url: e.target.value } as BinomLandingDetail)
          }
          className="h-8 text-xs"
        />
        <BinomUrlTokensPanel
          url={form.url ?? ''}
          onUrlChange={(next) =>
            onChange({ ...form, url: next } as BinomLandingDetail)
          }
        />
      </div>
    </OfferEditField>
    <OfferEditField label="Group">
      <SearchableSelect
        value={form.groupUuid ?? ''}
        onChange={(v) =>
          onChange({ ...form, groupUuid: String(v) } as BinomLandingDetail)
        }
        options={[{ value: '', label: 'No group' }, ...groupOptions]}
      />
    </OfferEditField>
    <OfferEditField label="Language">
      <SearchableSelect
        value={form.languageCode ?? ''}
        onChange={(v) =>
          onChange({ ...form, languageCode: String(v) } as BinomLandingDetail)
        }
        options={[{ value: '', label: 'Without language' }, ...languageOptions]}
      />
    </OfferEditField>
  </div>
);

export default BinomLanderEditBody;
